import React, { useState, useEffect, useRef, useCallback } from 'react';
import mqtt from 'mqtt';
import axios from 'axios';
import './Page15.css';

const BROKER = 'wss://kidr78home.duckdns.org/mqtt';
const API = 'https://kidr78home.duckdns.org/api/cogni';
const AUTH = 'kSuIlUmNaTiIeO5';
const hdrs = { headers: { Authorization: AUTH } };

function Page15() {
  // Core state
  const [status, setStatus] = useState('Connecting...');
  const [cogniState, setCogniState] = useState(null);
  const [emotions, setEmotions] = useState(null);
  const [resonance, setResonance] = useState(null);
  const [activity, setActivity] = useState(null);
  const [surprise, setSurprise] = useState(null);
  const [monologue, setMonologue] = useState([]);
  const [feed, setFeed] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [waitingReply, setWaitingReply] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  // DB-backed panels
  const [attention, setAttention] = useState(null);
  const [landscape, setLandscape] = useState(null);
  const [worldview, setWorldview] = useState(null);
  const [intents, setIntents] = useState(null);
  const [temporal, setTemporal] = useState(null);
  const [goals, setGoals] = useState(null);
  const [intuition, setIntuition] = useState(null);
  const [expandedWv, setExpandedWv] = useState(null);

  const clientRef = useRef(null);
  const feedEndRef = useRef(null);
  const maxFeed = 100;

  const addFeedItem = useCallback((item) => {
    setFeed(prev => [...prev.slice(-(maxFeed - 1)), { ...item, id: Date.now() + Math.random() }]);
  }, []);

  // Load all historical data on mount
  useEffect(() => {
    const load = async () => {
      try {
        const results = await Promise.allSettled([
          axios.get(`${API}/feed?limit=50`, hdrs),
          axios.get(`${API}/emotions`, hdrs),
          axios.get(`${API}/resonance`, hdrs),
          axios.get(`${API}/attention`, hdrs),
          axios.get(`${API}/landscape`, hdrs),
          axios.get(`${API}/worldview`, hdrs),
          axios.get(`${API}/intents`, hdrs),
          axios.get(`${API}/temporal`, hdrs),
          axios.get(`${API}/goals`, hdrs),
          axios.get(`${API}/intuition`, hdrs),
        ]);
        const [feedRes, emoRes, resRes, attRes, landRes, wvRes, intRes, tempRes, goalsRes, intuitionRes] = results;

        if (emoRes.status === 'fulfilled' && emoRes.value.data.engagement !== undefined)
          setEmotions(emoRes.value.data);
        if (resRes.status === 'fulfilled' && resRes.value.data.safety !== undefined)
          setResonance(resRes.value.data);
        if (attRes.status === 'fulfilled' && attRes.value.data.cloud)
          setAttention(attRes.value.data);
        if (landRes.status === 'fulfilled' && landRes.value.data.total_active !== undefined)
          setLandscape(landRes.value.data);
        if (wvRes.status === 'fulfilled' && wvRes.value.data.worldview)
          setWorldview(wvRes.value.data);
        if (intRes.status === 'fulfilled' && intRes.value.data.intents)
          setIntents(intRes.value.data.intents);
        if (tempRes.status === 'fulfilled' && tempRes.value.data.patterns)
          setTemporal(tempRes.value.data.patterns);
        if (goalsRes.status === 'fulfilled' && goalsRes.value.data.goals)
          setGoals(goalsRes.value.data.goals);
        if (intuitionRes.status === 'fulfilled' && intuitionRes.value.data.felt_sense !== undefined)
          setIntuition(intuitionRes.value.data);

        if (feedRes.status === 'fulfilled' && feedRes.value.data.items) {
          const items = feedRes.value.data.items.map(item => {
            const payload = item.payload || {};
            let type = 'status', data = {};
            if (item.subtopic === 'thoughts') {
              type = 'thought';
              data = { type: payload.type || item.msg_type, message: item.message || payload.message, timestamp: item.ts };
            } else if (item.subtopic === 'dreams') {
              type = 'dream';
              data = { narrative: item.narrative || payload.narrative, insights: payload.insights, message: payload.message, questions: payload.questions, cost: payload.cost, timestamp: item.ts };
            } else if (item.subtopic === 'status') {
              type = 'status';
              data = { state: payload.state, thought: payload.thought || item.message, event: payload.event, timestamp: item.ts };
              if (payload.state) setCogniState(payload);
            } else if (item.subtopic === 'response') {
              type = 'response';
              data = { question: payload.question, answer: payload.answer || item.message, timestamp: item.ts };
            }
            if (item.emotions) data.emotions = item.emotions;
            return { type, data, id: `hist-${item.ts}-${Math.random()}` };
          });
          setFeed(items);
        }
      } catch (err) { console.error('Failed to load Cogni history:', err); }
      finally { setLoadingHistory(false); }
    };
    load();
  }, []);

  // MQTT connection
  useEffect(() => {
    const client = mqtt.connect(BROKER);
    clientRef.current = client;
    client.on('connect', () => { setStatus('Connected'); client.subscribe('cogshell/#'); });
    client.on('error', () => setStatus('Connection Error'));
    client.on('close', () => setStatus('Disconnected'));

    client.on('message', (topic, msg) => {
      try {
        const data = JSON.parse(msg.toString());
        const sub = topic.replace('cogshell/', '');
        if (sub === 'status') { setCogniState(data); if (data.emotions) setEmotions(data.emotions); addFeedItem({ type: 'status', data }); }
        else if (sub === 'emotions') setEmotions(data);
        else if (sub === 'resonance') setResonance(data);
        else if (sub === 'activity') { setActivity(data); if (data.meta?.emotions) setEmotions(data.meta.emotions); }
        else if (sub === 'subconscious') setSurprise(data);
        else if (sub === 'monologue') setMonologue(prev => [...prev.slice(-9), data]);
        else if (sub === 'thoughts') { if (data.emotions) setEmotions(data.emotions); addFeedItem({ type: 'thought', data }); }
        else if (sub === 'dreams') { if (data.emotions) setEmotions(data.emotions); addFeedItem({ type: 'dream', data }); }
        else if (sub === 'response') { if (data.emotions) setEmotions(data.emotions); addFeedItem({ type: 'response', data }); setWaitingReply(false); }
      } catch (e) { console.error('Cogni MQTT parse error:', e); }
    });
    return () => client.end();
  }, [addFeedItem]);

  // Auto-scroll chat only (not inner life) when new messages arrive
  const prevChatCount = useRef(0);
  useEffect(() => {
    const chatItems = feed.filter(i => i.type === 'user' || i.type === 'response');
    if (chatItems.length > prevChatCount.current) {
      feedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevChatCount.current = chatItems.length;
  }, [feed]);

  const sendMessage = () => {
    const text = chatInput.trim();
    if (!text || !clientRef.current) return;
    clientRef.current.publish('cogshell/inbox', text);
    addFeedItem({ type: 'user', data: { message: text, timestamp: new Date().toISOString() } });
    setChatInput('');
    setWaitingReply(true);
  };

  // Periodic refresh for gut feeling and goals (every 2 minutes)
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [gRes, iRes] = await Promise.allSettled([
          axios.get(`${API}/goals`, hdrs),
          axios.get(`${API}/intuition`, hdrs),
        ]);
        if (gRes.status === 'fulfilled' && gRes.value.data.goals) setGoals(gRes.value.data.goals);
        if (iRes.status === 'fulfilled' && iRes.value.data.felt_sense !== undefined) setIntuition(iRes.value.data);
      } catch {}
    }, 120000);
    return () => clearInterval(interval);
  }, []);

  const fmt = (ts) => { if (!ts) return ''; try { return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
  const fmtDate = (ts) => { if (!ts) return ''; try { const d = new Date(ts); return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${fmt(ts)}`; } catch { return ''; } };

  // === RENDERERS ===

  // Emotion bar (0-1 range)
  const emoBar = (label, value, color) => (
    <div className="emo-row" key={label}>
      <span className="emo-label">{label}</span>
      <div className="emo-track"><div className="emo-fill" style={{ width: `${(value || 0) * 100}%`, background: color }} /></div>
      <span className="emo-val">{((value || 0) * 100).toFixed(0)}%</span>
    </div>
  );

  // Resonance bar (-1 to +1 range, centered)
  const resBar = (label, value, negLabel, posLabel) => {
    const v = value || 0;
    const pct = ((v + 1) / 2) * 100; // map -1..+1 to 0..100
    const color = v >= 0 ? `rgba(46, 204, 113, ${0.4 + v * 0.6})` : `rgba(231, 76, 60, ${0.4 + Math.abs(v) * 0.6})`;
    return (
      <div className="res-row" key={label}>
        <span className="res-neg">{negLabel}</span>
        <div className="res-track">
          <div className="res-center" />
          <div className="res-fill" style={{ left: Math.min(pct, 50) + '%', width: Math.abs(pct - 50) + '%', background: color }} />
        </div>
        <span className="res-pos">{posLabel}</span>
      </div>
    );
  };

  // Surprise gauge
  const surpriseLevel = (v) => v < 0.2 ? 'expected' : v < 0.5 ? 'curious' : v < 0.75 ? 'surprised' : 'very surprised';
  const surpriseColor = (v) => v < 0.2 ? '#2ecc71' : v < 0.5 ? '#f39c12' : v < 0.75 ? '#e67e22' : '#e74c3c';

  // Activity helpers
  const actIcon = (s) => ({ resting: '😴', pulsing: '💓', processing_inbox: '📬', thinking: '🧠', idle: '✅', dreaming: '🌙', investigating: '🔍', running_task: '⚙️' }[s] || '⚡');
  const actColor = (s) => ({ resting: '#9b59b6', pulsing: '#e74c3c', processing_inbox: '#f39c12', thinking: '#3498db', idle: '#2ecc71', dreaming: '#9b59b6', investigating: '#e67e22', running_task: '#1abc9c' }[s] || '#95a5a6');
  const stateIcon = (s) => ({ resting: '😴', processing: '⚡', idle: '💭', offline: '🔌' }[s] || '❓');

  // Stability label
  const stabilityLabel = (v) => v < 0.3 ? 'forming' : v < 0.7 ? 'developing' : 'deeply held';

  // Rich text renderer
  const renderRichText = (text) => {
    if (!text) return null;
    const segments = text.split(/(```[\s\S]*?```)/g);
    return segments.map((segment, si) => {
      if (segment.startsWith('```') && segment.endsWith('```')) {
        const inner = segment.slice(3, -3);
        const nl = inner.indexOf('\n');
        const code = nl >= 0 ? inner.slice(nl + 1) : inner;
        return <pre className="rich-code-block" key={si}><code>{code.trim()}</code></pre>;
      }
      return segment.split(/\n\n+/).map((para, pi) => {
        const key = `${si}-${pi}`;
        const lines = para.split('\n');

        // Detect markdown tables: lines with | separators
        const tableLines = lines.filter(l => l.trim().startsWith('|') && l.trim().endsWith('|'));
        if (tableLines.length >= 2) {
          // Parse table: skip separator rows (|---|---|)
          const dataRows = tableLines.filter(l => !/^\|[\s\-:]+\|$/.test(l.trim()));
          if (dataRows.length >= 1) {
            const parseRow = (row) => row.split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1).map(c => c.trim());
            const headers = parseRow(dataRows[0]);
            const bodyRows = dataRows.slice(1).map(parseRow);
            return (
              <table className="rich-table" key={key}>
                <thead><tr>{headers.map((h, i) => <th key={i}>{renderInline(h)}</th>)}</tr></thead>
                <tbody>{bodyRows.map((row, ri) => <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{renderInline(cell)}</td>)}</tr>)}</tbody>
              </table>
            );
          }
        }

        // Detect inline tables: "| val | val | val |" pattern mixed with text
        // (model sometimes outputs tables without proper newline separation)
        const pipeLines = lines.filter(l => (l.match(/\|/g) || []).length >= 3);
        if (pipeLines.length >= 2 && pipeLines.length === lines.filter(l => l.trim()).length) {
          const dataRows = pipeLines.filter(l => !/^[\s\-:|]+$/.test(l.trim()));
          if (dataRows.length >= 1) {
            const parseRow = (row) => row.split('|').map(c => c.trim()).filter(c => c);
            const headers = parseRow(dataRows[0]);
            const bodyRows = dataRows.slice(1).map(parseRow);
            return (
              <table className="rich-table" key={key}>
                <thead><tr>{headers.map((h, i) => <th key={i}>{renderInline(h)}</th>)}</tr></thead>
                <tbody>{bodyRows.map((row, ri) => <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{renderInline(cell)}</td>)}</tr>)}</tbody>
              </table>
            );
          }
        }

        const isList = lines.every(l => /^\s*[\*\-•]\s/.test(l) || !l.trim());
        if (isList) {
          return <ul className="rich-list" key={key}>{lines.filter(l => l.trim()).map((li, i) => <li key={i}>{renderInline(li.replace(/^\s*[\*\-•]\s*/, ''))}</li>)}</ul>;
        }
        const parts = []; let cur = [];
        lines.forEach((line, li) => {
          if (/^\s*[\*\-•]\s/.test(line)) {
            if (cur.length) { parts.push({ t: 'p', c: cur.join('\n'), k: `t${li}` }); cur = []; }
            parts.push({ t: 'li', c: line.replace(/^\s*[\*\-•]\s*/, ''), k: `l${li}` });
          } else cur.push(line);
        });
        if (cur.length) parts.push({ t: 'p', c: cur.join('\n'), k: 'tf' });
        if (parts.some(p => p.t === 'li')) {
          return <div key={key} className="rich-block">{parts.map(p => p.t === 'li' ? <div key={p.k} className="rich-bullet">{renderInline(p.c)}</div> : <p key={p.k} className="rich-para">{renderInline(p.c)}</p>)}</div>;
        }
        return <p className="rich-para" key={key}>{renderInline(para)}</p>;
      });
    });
  };

  const renderInline = (text) => {
    if (!text) return null;
    return text.split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g).map((p, i) => {
      if (p.startsWith('`') && p.endsWith('`') && p.length > 1) return <code className="rich-inline-code" key={i}>{p.slice(1, -1)}</code>;
      if (p.startsWith('**') && p.endsWith('**') && p.length > 4) return <strong key={i}>{p.slice(2, -2)}</strong>;
      if (p.startsWith('*') && p.endsWith('*') && p.length > 2) return <em key={i}>{p.slice(1, -1)}</em>;
      return p;
    });
  };

  // Feed item renderer
  const renderFeedItem = (item) => {
    switch (item.type) {
      case 'thought': {
        const isTaskResult = item.data.type === 'task_result';
        return (<div className={`feed-card thought ${isTaskResult ? 'task-result' : ''}`} key={item.id}><div className="feed-badge">{isTaskResult ? '⚙️ Task Result' : '💭'} {!isTaskResult && (item.data.type === 'dream_message' ? 'Dream thought' : 'Thought')}</div><div className="feed-text rich-content">{renderRichText(item.data.message)}</div><span className="feed-time">{fmt(item.data.timestamp)}</span></div>);
      }
      case 'dream':
        return (<div className="feed-card dream" key={item.id}><div className="feed-badge">🌙 Dream</div><div className="feed-text rich-content">{renderRichText(item.data.narrative)}</div>
          {item.data.insights?.length > 0 && <div className="dream-insights">{item.data.insights.map((ins, i) => <span key={i} className="insight-tag">💡 {ins}</span>)}</div>}
          {item.data.questions?.length > 0 && <div className="dream-questions">{item.data.questions.map((q, i) => <span key={i} className="question-tag">❓ {q}</span>)}</div>}
          {item.data.message && <p className="dream-msg">📨 "{item.data.message}"</p>}
          {item.data.cost > 0 && <span className="dream-cost">💰 ${item.data.cost.toFixed(4)}</span>}
          <span className="feed-time">{fmt(item.data.timestamp)}</span></div>);
      case 'status':
        return (<div className={`feed-card status-card ${isTaskStatus(item) ? 'task-status' : ''}`} key={item.id}><div className="feed-badge">{stateIcon(item.data.state)} {item.data.state}{item.data.event ? ` · ${item.data.event}` : ''}</div>{item.data.thought && <p className="feed-text">{item.data.thought}</p>}<span className="feed-time">{fmt(item.data.timestamp)}</span></div>);
      case 'user':
        return (<div className="feed-card user-msg" key={item.id}><div className="feed-badge">🧑 You</div><p className="feed-text">{item.data.message}</p><span className="feed-time">{fmt(item.data.timestamp)}</span></div>);
      case 'response':
        return (<div className="feed-card cogni-reply" key={item.id}><div className="feed-badge">🤖 Cogni</div>{item.data.question && <p className="feed-question">Re: "{item.data.question}"</p>}<div className="feed-text rich-content">{renderRichText(item.data.answer)}</div><span className="feed-time">{fmt(item.data.timestamp)}</span></div>);
      default: return null;
    }
  };

  // Helpers to identify task-related feed items
  const isTaskStatus = (item) => {
    if (item.type !== 'status') return false;
    const s = (item.data.state || '').toLowerCase();
    const e = (item.data.event || '').toLowerCase();
    return s.includes('task') || e.includes('task') || s === 'investigating' || s === 'running_task';
  };

  const isTaskItem = (item) => {
    if (item.type === 'thought' && item.data.type === 'task_result') return true;
    if (isTaskStatus(item)) return true;
    return false;
  };

  // Temporal heatmap: build grid data
  const temporalGrid = () => {
    if (!temporal?.length) return null;
    const topTags = [...new Set(temporal.map(p => p.tag))].slice(0, 8);
    const grid = {};
    topTags.forEach(tag => { grid[tag] = new Array(24).fill(0); });
    temporal.forEach(p => { if (grid[p.tag]) grid[p.tag][p.hour] = (grid[p.tag][p.hour] || 0) + p.weight; });
    const maxW = Math.max(...Object.values(grid).flat(), 0.01);
    return { topTags, grid, maxW };
  };

  // Attention cloud: sort by score
  const attentionWords = () => {
    if (!attention?.cloud) return [];
    return Object.entries(attention.cloud).sort((a, b) => b[1] - a[1]).slice(0, 30);
  };

  // === RENDER ===
  const tg = temporalGrid();
  const aw = attentionWords();

  return (
    <div className="cogni-page">
      {/* Header */}
      <div className="cogni-header">
        <h1 className="cogni-title">🧠 Cogni</h1>
        <div className="cogni-status-row">
          <span className={`conn-badge ${status === 'Connected' ? 'on' : ''}`}><span className="conn-dot" /> {status}</span>
          {cogniState && <span className="state-badge">{stateIcon(cogniState.state)} {cogniState.state}</span>}
        </div>
      </div>

      {/* Top row: Mood + Resonance + Attention Cloud */}
      <div className="cogni-grid">
        <div className="cogni-top-row">
          {/* Mood (7 emotions) */}
          <div className="panel mood-panel">
            <h3 className="panel-title">Mood</h3>
            {emotions ? <>
              {emoBar('Engagement', emotions.engagement, '#3498db')}
              {emoBar('Confidence', emotions.confidence, '#2ecc71')}
              {emoBar('Frustration', emotions.frustration, '#e74c3c')}
              {emoBar('Curiosity', emotions.curiosity, '#f39c12')}
              {emoBar('Fatigue', emotions.fatigue, '#9b59b6')}
              {emoBar('Boredom', emotions.boredom, '#d68910')}
              {emoBar('Anger', emotions.anger, '#c0392b')}
              {emoBar('Unhappy', emotions.unhappy, '#8e44ad')}
            </> : <p className="panel-waiting">Waiting for data...</p>}
          </div>

          {/* Resonance */}
          <div className="panel resonance-panel">
            <h3 className="panel-title">Resonance</h3>
            {resonance ? <>
              {resBar('safety', resonance.safety, 'uneasy', 'secure')}
              {resBar('energy', resonance.energy, 'drained', 'energized')}
              {resBar('connection', resonance.connection, 'isolated', 'bonded')}
              {resBar('novelty', resonance.novelty, 'routine', 'discovering')}
              {resBar('tension', resonance.tension, 'calm', 'urgent')}
            </> : <p className="panel-waiting">Waiting for data...</p>}

            {/* Surprise meter */}
            <div className="surprise-section">
              <h4 className="panel-subtitle">Surprise</h4>
              {surprise ? <>
                <div className="surprise-gauge">
                  <div className="surprise-arc">
                    <div className="surprise-fill" style={{ width: `${(surprise.surprise || 0) * 100}%`, background: surpriseColor(surprise.surprise || 0) }} />
                  </div>
                  <span className="surprise-label" style={{ color: surpriseColor(surprise.surprise || 0) }}>{surpriseLevel(surprise.surprise || 0)}</span>
                </div>
                {surprise.anomalies?.length > 0 && (
                  <div className="anomaly-list">{surprise.anomalies.slice(0, 3).map((a, i) => <span key={i} className="anomaly-tag">⚠️ {a}</span>)}</div>
                )}
              </> : <span className="panel-dim">No surprises</span>}
            </div>
          </div>

          {/* Attention Cloud */}
          <div className="panel attention-panel">
            <h3 className="panel-title">Attention Cloud</h3>
            {aw.length > 0 ? (
              <div className="word-cloud">
                {aw.map(([word, score], i) => {
                  const size = 0.7 + (score / (aw[0]?.[1] || 1)) * 0.9;
                  const opacity = 0.4 + (score / (aw[0]?.[1] || 1)) * 0.6;
                  return <span key={i} className="cloud-word" style={{ fontSize: `${size}rem`, opacity }}>{word}</span>;
                })}
              </div>
            ) : <p className="panel-waiting">Waiting for data...</p>}
          </div>
        </div>

        {/* Gut Feeling + Goals row */}
        <div className="cogni-mid-row">
          {/* Gut Feeling */}
          <div className="panel intuition-panel">
            <h3 className="panel-title">🫀 Gut Feeling</h3>
            {intuition && intuition.felt_sense ? <>
              <p className="intuition-sense">"{intuition.felt_sense}"</p>
              <div className="intuition-meta">
                <div className="intuition-urgency">
                  <span className="intuition-label">Urgency</span>
                  <div className="emo-track"><div className="emo-fill" style={{ width: `${(intuition.urgency || 0) * 100}%`, background: intuition.urgency > 0.7 ? '#e74c3c' : intuition.urgency > 0.4 ? '#f39c12' : '#2ecc71' }} /></div>
                  <span className="emo-val">{((intuition.urgency || 0) * 100).toFixed(0)}%</span>
                </div>
                {intuition.pull_toward && <p className="intuition-pull">→ Drawn to: {intuition.pull_toward}</p>}
                {intuition.unease_about && <p className="intuition-unease">⚠ Uneasy about: {intuition.unease_about}</p>}
              </div>
              {intuition.history?.length > 1 && <>
                <h4 className="panel-subtitle" style={{ marginTop: '0.6rem' }}>Recent Feelings</h4>
                <div className="intuition-history">
                  {intuition.history.slice(-5).reverse().slice(1).map((h, i) => (
                    <div key={i} className="intuition-hist-item">
                      <span className="intuition-hist-urgency" style={{ background: h.urgency > 0.7 ? '#e74c3c' : h.urgency > 0.4 ? '#f39c12' : '#2ecc71', width: '6px', height: '6px', borderRadius: '50%', display: 'inline-block', flexShrink: 0 }} />
                      <span className="intuition-hist-text">{h.felt_sense?.slice(0, 80)}</span>
                      <span className="intuition-hist-time">{fmt(new Date(h.timestamp * 1000).toISOString())}</span>
                    </div>
                  ))}
                </div>
              </>}
            </> : <p className="panel-waiting">Waiting for gut feeling...</p>}
          </div>

          {/* Goals */}
          <div className="panel goals-panel">
            <h3 className="panel-title">🎯 Goals</h3>
            {goals && goals.length > 0 ? (
              <div className="goals-list">
                {goals.filter(g => g.status === 'active').map(g => (
                  <div key={g.id} className="goal-item active">
                    <div className="goal-header">
                      <span className="goal-topic">{g.topic}</span>
                      <span className="goal-sessions">{g.session_count} sessions</span>
                    </div>
                    <div className="goal-bars">
                      <div className="goal-bar-row">
                        <span className="goal-bar-label">Investment</span>
                        <div className="emo-track"><div className="emo-fill" style={{ width: `${(g.emotional_investment || 0) * 100}%`, background: '#bb86fc' }} /></div>
                      </div>
                      <div className="goal-bar-row">
                        <span className="goal-bar-label">Priority</span>
                        <div className="emo-track"><div className="emo-fill" style={{ width: `${(g.priority || 0) * 100}%`, background: '#f39c12' }} /></div>
                      </div>
                    </div>
                    {g.progress?.length > 0 && <p className="goal-progress">{g.progress[g.progress.length - 1].note?.slice(0, 80)}</p>}
                    <span className="goal-meta">src: {g.source} · last: {g.last_worked_on ? fmtDate(g.last_worked_on) : 'never'}</span>
                  </div>
                ))}
                {goals.filter(g => g.status !== 'active').slice(0, 3).map(g => (
                  <div key={g.id} className={`goal-item ${g.status}`}>
                    <span className="goal-status-icon">{g.status === 'completed' ? '✅' : '❌'}</span>
                    <span className="goal-topic faded">{g.topic}</span>
                  </div>
                ))}
              </div>
            ) : <p className="panel-waiting">No goals yet — seeds need time to grow</p>}
          </div>
        </div>

        {/* Bottom row: Memory + Temporal + Intents */}
        <div className="cogni-bottom-row">
          {/* Memory Landscape */}
          <div className="panel landscape-panel">
            <h3 className="panel-title">Memory Landscape</h3>
            {landscape ? <>
              <div className="landscape-counts">
                <span className="lc-active">🟢 {landscape.total_active} active</span>
                <span className="lc-dormant">💤 {landscape.total_dormant} sleeping</span>
              </div>
              {landscape.themes?.length > 0 && <>
                <h4 className="panel-subtitle">Top Themes</h4>
                <div className="theme-bars">
                  {landscape.themes.slice(0, 8).map(([tag, count], i) => (
                    <div key={i} className="theme-row">
                      <span className="theme-label">{tag}</span>
                      <div className="theme-track"><div className="theme-fill" style={{ width: `${(count / (landscape.themes[0]?.[1] || 1)) * 100}%` }} /></div>
                      <span className="theme-count">{count}</span>
                    </div>
                  ))}
                </div>
              </>}
              {landscape.clusters?.length > 0 && <>
                <h4 className="panel-subtitle">Clusters</h4>
                <div className="cluster-pills">
                  {landscape.clusters.slice(0, 6).map((c, i) => (
                    <div key={i} className="cluster-group">
                      {c.tags?.map((t, j) => <span key={j} className="cluster-tag">{t}</span>)}
                    </div>
                  ))}
                </div>
              </>}
              {landscape.type_distribution && <>
                <h4 className="panel-subtitle">Types</h4>
                <div className="type-dist">
                  {Object.entries(landscape.type_distribution).map(([type, count], i) => (
                    <span key={i} className="type-pill">{type}: {count}</span>
                  ))}
                </div>
              </>}
            </> : <p className="panel-waiting">Waiting for data...</p>}
          </div>

          {/* Temporal Patterns */}
          <div className="panel temporal-panel">
            <h3 className="panel-title">Temporal Patterns</h3>
            {tg ? (
              <div className="heatmap">
                <div className="heatmap-hours">
                  {[0,3,6,9,12,15,18,21].map(h => <span key={h} className="heatmap-hour">{h}</span>)}
                </div>
                {tg.topTags.map(tag => (
                  <div key={tag} className="heatmap-row">
                    <span className="heatmap-tag">{tag}</span>
                    <div className="heatmap-cells">
                      {tg.grid[tag].map((w, h) => (
                        <div key={h} className="heatmap-cell" style={{ opacity: 0.1 + (w / tg.maxW) * 0.9 }} title={`${tag} @ ${h}:00 — ${w.toFixed(1)}`} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="panel-waiting">Waiting for data...</p>}

            {/* Inner Monologue */}
            {monologue.length > 0 && <>
              <h4 className="panel-subtitle" style={{ marginTop: '0.75rem' }}>Inner Monologue</h4>
              <div className="monologue-feed">
                {monologue.filter(m => (m.salience || 0) > 0.3).map((m, i) => (
                  <div key={i} className="monologue-item">
                    {m.anomalies?.map((a, j) => <span key={j} className="anomaly-tag">⚠️ {a.description}</span>)}
                    {m.associations?.map((a, j) => <span key={j} className="assoc-tag">💭 {a.memory}</span>)}
                  </div>
                ))}
              </div>
            </>}
          </div>

          {/* Intent Queue */}
          <div className="panel intent-panel">
            <h3 className="panel-title">Intent Queue</h3>
            {intents?.length > 0 ? (
              <div className="intent-list">
                {intents.map((it, i) => (
                  <div key={i} className={`intent-item ${it.status}`}>
                    <span className="intent-icon">{it.status === 'pending' ? '⏳' : it.status === 'completed' || it.status === 'done' ? '✅' : '❌'}</span>
                    <div className="intent-body">
                      <span className="intent-text">{it.intent}</span>
                      <div className="intent-meta">
                        <span>{fmtDate(it.created_at)}</span>
                        <span>{it.attempts}/{it.max_attempts}</span>
                      </div>
                      <div className="intent-progress-track"><div className="intent-progress-fill" style={{ width: `${(it.attempts / (it.max_attempts || 1)) * 100}%` }} /></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="panel-waiting">No pending intents</p>}
          </div>
        </div>

        {/* Task Outputs — dedicated panel for task results (full width, prominent) */}
        <div className="panel task-outputs-panel">
          <h3 className="panel-title">⚙️ Task Outputs</h3>
          <div className="feed-container task-outputs-feed">
            {feed.filter(isTaskItem).length === 0 ? (
              <div className="feed-empty"><span>⚙️</span><p>No task outputs yet</p></div>
            ) : feed.filter(isTaskItem).slice().reverse().map(renderFeedItem)}
          </div>
        </div>

        {/* Inner Life — thoughts, dreams, status (excluding task items) */}
        <div className="panel inner-life-panel">
          <h3 className="panel-title">🌊 Inner Life</h3>
          <div className="feed-container inner-life-feed">
            {feed.filter(i => (i.type === 'thought' || i.type === 'dream' || i.type === 'status') && !isTaskItem(i)).length === 0 ? (
              <div className="feed-empty"><span>🌌</span><p>Waiting for Cogni's thoughts...</p></div>
            ) : feed.filter(i => (i.type === 'thought' || i.type === 'dream' || i.type === 'status') && !isTaskItem(i)).slice().reverse().map(renderFeedItem)}
          </div>
        </div>

        {/* Worldview & Identity */}
        {worldview && (
          <div className="panel worldview-panel">
            <h3 className="panel-title">Worldview & Identity</h3>
            <div className="wv-cards">
              {[...(worldview.worldview || []).map(w => ({ ...w, name: w.section, src: 'worldview' })),
                ...(worldview.self_authored || []).map(s => ({ ...s, name: s.block_name, src: 'identity' }))
              ].map((item, i) => (
                <div key={i} className={`wv-card ${expandedWv === i ? 'expanded' : ''}`} onClick={() => setExpandedWv(expandedWv === i ? null : i)}>
                  <div className="wv-card-header">
                    <span className="wv-name">{item.src === 'identity' ? '🪞' : '🌍'} {item.name}</span>
                    <div className="wv-stability">
                      <div className="wv-stab-track"><div className="wv-stab-fill" style={{ width: `${(item.stability || 0) * 100}%` }} /></div>
                      <span className="wv-stab-label">{stabilityLabel(item.stability || 0)}</span>
                    </div>
                    <span className="wv-rev">rev {item.revision_count || 0}</span>
                  </div>
                  {expandedWv === i && (
                    <div className="wv-card-body">
                      <div className="wv-content rich-content">{renderRichText(item.content)}</div>
                      <span className="wv-updated">{fmtDate(item.updated_at)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat — direct conversation (always last, full width) */}
        <div className="panel chat-panel">
          <h3 className="panel-title">💬 Chat</h3>
          <div className="feed-container chat-feed">
            {loadingHistory ? (
              <div className="feed-empty"><span>⏳</span><p>Loading history...</p></div>
            ) : feed.filter(i => i.type === 'user' || i.type === 'response').length === 0 ? (
              <div className="feed-empty"><span>💬</span><p>Send a message to start chatting</p></div>
            ) : feed.filter(i => i.type === 'user' || i.type === 'response').map(renderFeedItem)}
            {waitingReply && <div className="feed-card cogni-typing"><span className="typing-dots">🤖 Cogni is thinking<span className="dots">...</span></span></div>}
            <div ref={feedEndRef} />
          </div>

          {/* Activity strip */}
          <div className={`activity-strip ${activity ? activity.state : 'idle'}`}>
            <span className="activity-icon" style={{ color: actColor(activity ? activity.state : 'idle') }}>{actIcon(activity ? activity.state : 'idle')}</span>
            <span className="activity-detail">{activity ? activity.detail : 'Waiting for activity...'}</span>
            {activity?.state === 'thinking' && activity.meta?.step && <span className="activity-step-badge">{activity.meta.step}/{activity.meta.max_steps || 15}</span>}
            {activity?.meta?.cost > 0 && <span className="activity-cost">💰 ${activity.meta.cost.toFixed(4)}</span>}
            {activity?.state === 'thinking' && activity.meta?.step && (
              <div className="activity-strip-progress"><div className="activity-progress-fill" style={{ width: `${(activity.meta.step / (activity.meta.max_steps || 15)) * 100}%`, background: actColor(activity.state) }} /></div>
            )}
          </div>

          <div className="chat-bar">
            <textarea className="chat-input" placeholder="Message Cogni... (Shift+Enter for new line)" value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              disabled={waitingReply} rows={1}
              onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
            />
            <button className="chat-send" onClick={sendMessage} disabled={!chatInput.trim() || waitingReply} aria-label="Send message">📨</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Page15;
